/**
 * Detect plausible file extension from clipboard text content.
 * Uses high-confidence heuristics — rename is always offered as fallback.
 */
export function detectFileExtension(text: string): string {
    const trimmed = text.trimStart()
    const first500 = trimmed.slice(0, 500)
    const firstLine = trimmed.split(/\r?\n/)[0]

    // JSON — parse validates
    if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length < 2_000_000) {
        try { JSON.parse(trimmed); return '.json' } catch { /* not valid JSON */ }
    }

    // HTML / XML / SVG
    if (/^<(!DOCTYPE|html)/i.test(trimmed)) return '.html'
    if (/^<\?xml/i.test(trimmed)) return '.xml'
    if (/^<svg[\s>]/i.test(trimmed)) return '.svg'

    // Shebang
    if (trimmed.startsWith('#!/')) {
        if (/python/i.test(firstLine)) return '.py'
        if (/node|deno|bun/i.test(firstLine)) return '.js'
        return '.sh'
    }

    // CSS — distinctive at-rules or root selectors
    if (/^(@charset|@import\s|@font-face|@keyframes|:root\s*\{)/m.test(first500)) return '.css'

    // SQL — DDL/DML keywords at start
    if (/^(SELECT\s|INSERT\s|UPDATE\s|DELETE\s|CREATE\s+(TABLE|INDEX|VIEW)|ALTER\s|DROP\s)/i.test(trimmed)) return '.sql'

    // C# — using + namespace/class in first 500 chars
    if (/^using\s+[\w.]+;/m.test(first500) && /\b(namespace|class)\b/.test(first500)) return '.cs'

    // TypeScript/JavaScript — ES import with quoted module source, or export declarations
    if (/^import\s+.*from\s+['"]|^import\s*\{|^export\s+(default\s+)?(function|class|const|interface|type)\s/m.test(first500)) return '.ts'

    // Python — from/import or def/class
    if (/^(from\s+\w[\w.]*\s+import\s|import\s+\w[\w.,\s]*$)/m.test(firstLine)) return '.py'
    if (/^(def\s+\w+\s*\(|class\s+\w+\s*[:(])/m.test(firstLine)) return '.py'

    // Fallback
    return '.md'
}
